import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * Co-located controller that manages the auto-dismiss timer lifecycle for `asc-alert`.
 * Starts a `setTimeout` when the host connects (or when `autoDismiss`/`dismissDuration`
 * changes at runtime) if `autoDismiss` is enabled. Clears the timer when the host
 * disconnects to prevent memory leaks.
 */
export class AutoDismissController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & { autoDismiss: boolean; dismissDuration: number };
  private readonly _onDismiss: () => void;
  private _timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    host: ReactiveControllerHost & { autoDismiss: boolean; dismissDuration: number },
    onDismiss: () => void,
  ) {
    this._host = host;
    this._onDismiss = onDismiss;
    host.addController(this);
  }

  private _syncTimer(): void {
    if (!this._host.autoDismiss) {
      if (this._timer !== undefined) {
        clearTimeout(this._timer);
        this._timer = undefined;
      }
      return;
    }

    if (this._timer !== undefined) {
      clearTimeout(this._timer);
    }

    this._timer = setTimeout(() => {
      this._onDismiss();
    }, this._host.dismissDuration);
  }

  hostConnected(): void {
    this._syncTimer();
  }

  hostUpdated(): void {
    this._syncTimer();
  }

  hostDisconnected(): void {
    if (this._timer !== undefined) {
      clearTimeout(this._timer);
      this._timer = undefined;
    }
  }
}
